import React, { useState } from "react";
import { base44 } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { MapPin, X } from "lucide-react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

const FILTERS = [
  { label: "All Projects", value: "all" },
  { label: "Kitchen", value: "kitchen" },
  { label: "Bathroom", value: "bathroom" },
  { label: "Basement", value: "basement" },
  { label: "Whole-House", value: "whole-house" },
  { label: "Exterior", value: "exterior" },
  { label: "ADU", value: "adu" },
  { label: "Outdoor", value: "outdoor-living" },
  { label: "Historic", value: "historic-restoration" },
  { label: "Aging-in-Place", value: "aging-in-place" },
  { label: "Other", value: "other" },
];

const SERVICE_LABELS = {
  kitchen: "Kitchen",
  bathroom: "Bathroom",
  basement: "Basement",
  "whole-house": "Whole-House",
  exterior: "Exterior",
  adu: "ADU",
  "outdoor-living": "Outdoor Living",
  "historic-restoration": "Historic Restoration",
  "aging-in-place": "Aging-in-Place",
  other: "Other",
};

export default function Portfolio() {
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const { data: projects = [], isLoading } = useQuery({
    queryKey: ["portfolio-all"],
    queryFn: () => base44.entities.PortfolioProjects.list("display_order", 50),
    initialData: [],
  });

  // Only show projects that actually have an image. Placeholder rows live
  // in D1 (so Yoni can drop in photos later) but stay hidden until then.
  const withImages = projects.filter(
    (p) => p.after_image || (p.gallery_images && p.gallery_images.length > 0) || p.before_image,
  );
  const filtered = filter === "all" ? withImages : withImages.filter((p) => p.service_type === filter);

  if (isLoading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-muted border-t-accent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Hero */}
      <section className="bg-primary py-24 px-4">
        <div className="max-w-7xl mx-auto">
          <p className="font-body text-accent text-sm font-semibold uppercase tracking-[0.18em] mb-4">Our Work</p>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white max-w-2xl leading-tight">
            Projects Gallery
          </h1>
          <p className="font-body text-white/65 mt-4 max-w-xl text-lg">
            500+ completed projects across Baltimore County since 2010. Browse by service type.
          </p>
        </div>
      </section>

      {/* Filters */}
      <div className="sticky top-[88px] z-30 bg-background border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex gap-2 overflow-x-auto no-scrollbar">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`px-4 py-2 rounded-full font-body text-sm font-medium whitespace-nowrap transition-all ${
                filter === f.value
                  ? "bg-accent text-white"
                  : "bg-secondary text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {filtered.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground font-body">
            No projects found for this category yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((project, i) => {
              const img = project.after_image || (project.gallery_images && project.gallery_images[0]) || project.before_image;
              return (
                <motion.div
                  key={project.id}
                  layout
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: Math.min(i * 0.03, 0.3) }}
                >
                  <button
                    onClick={() => setSelected(project)}
                    className="group w-full text-left bg-card rounded-xl overflow-hidden border border-border hover:shadow-lg transition-all duration-300"
                  >
                    <div className="aspect-[4/3] overflow-hidden bg-muted">
                      {img ? (
                        <img loading="lazy" decoding="async" src={img} alt={project.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm font-body">Upload hero image</div>
                      )}
                    </div>
                    <div className="p-5">
                      <span className="font-body text-xs font-semibold text-accent uppercase tracking-wide">
                        {SERVICE_LABELS[project.service_type] || project.service_type}
                      </span>
                      <h3 className="font-heading text-lg font-bold text-foreground mt-1 mb-1 group-hover:text-accent transition-colors">{project.title}</h3>
                      {project.location && (
                        <p className="flex items-center gap-1 font-body text-sm text-muted-foreground">
                          <MapPin className="w-3.5 h-3.5" /> {project.location}
                        </p>
                      )}
                    </div>
                  </button>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
            onClick={() => setSelected(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-card rounded-2xl overflow-hidden max-w-3xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {(selected.after_image || (selected.gallery_images && selected.gallery_images[0])) && (
                <img loading="lazy" decoding="async"
                  src={selected.after_image || selected.gallery_images[0]}
                  alt={selected.title}
                  className="w-full aspect-video object-cover"
                />
              )}
              <div className="p-7">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <span className="font-body text-xs font-semibold text-accent uppercase tracking-wide">
                      {SERVICE_LABELS[selected.service_type] || selected.service_type}
                    </span>
                    <h2 className="font-heading text-2xl font-bold text-foreground mt-1">{selected.title}</h2>
                    {selected.location && (
                      <p className="flex items-center gap-1 font-body text-sm text-muted-foreground mt-1">
                        <MapPin className="w-3.5 h-3.5" /> {selected.location}
                      </p>
                    )}
                  </div>
                  <button onClick={() => setSelected(null)} className="p-2 hover:bg-secondary rounded-lg transition-colors flex-shrink-0">
                    <X className="w-5 h-5 text-muted-foreground" />
                  </button>
                </div>
                {selected.description && (
                  <p className="font-body text-muted-foreground leading-relaxed mb-5">{selected.description}</p>
                )}
                {selected.completion_date && (
                  <p className="font-body text-xs text-muted-foreground">
                    Completed: {new Date(selected.completion_date).toLocaleDateString("en-US", { month: "long", year: "numeric" })}
                  </p>
                )}
                {selected.gallery_images && selected.gallery_images.length > 1 && (
                  <div className="mt-5 grid grid-cols-3 gap-2">
                    {selected.gallery_images.map((img, i) => (
                      <img loading="lazy" decoding="async" key={i} src={img} alt={`${selected.title} ${i + 1}`} className="rounded-lg aspect-square object-cover" />
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}